import { Droplet, AlertCircle, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { useState, useMemo, useEffect } from "react";
import { getAnomalyCategory, getWaterLeak } from "../services";
import { useNavigate } from "react-router-dom";
import { formatDateToYYYYMMDD } from "../utils/dateLimits";
import { useDistrict } from "../utils/DistrictContext";
import { Spin } from "antd";

// Helper for Date Formatting: 05, Aug 2025
const formatDateCustom = (dateStr) => {
  if (!dateStr) return "N/A";

  const date = new Date(dateStr);

  // Use 'long' for the full month name (e.g., "March")
  const month = date.toLocaleString("en-US", { month: "long" });
  const day = date.getDate();
  const year = date.getFullYear();

  // Return in the format: Month Day, Year
  return `${month} ${day}, ${year}`;
};

// Helper to safely parse and round numbers
const safeRound = (val) => {
  if (val === null || val === undefined) return 0;
  const cleaned = String(val).replace(/[$,GPH\s,]/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : Math.round(num);
};

export function LeakFlow({ onClick }) {
  const navigate = useNavigate();
  const selectedDistrict = useDistrict();
  const { theme, colors } = useTheme();
  const PIE_COLORS = [
    "#ff6b6b", // red
    "#feca57", // yellow
    "#48dbfb", // cyan
    "#1dd1a1", // green
    "#5f27cd", // purple
    "#ff9ff3", // pink
    "#ff9f43", // orange
  ];

  const [selectedMeterType, setSelectedMeterType] = useState("ALL");
  const [selectedTimePeriod, setSelectedTimePeriod] = useState("Current");
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [dataLeak, setDataLeak] = useState([]);
  const [leakTableData, setLeakTableData] = useState([]);
  const [anomalyPieData, setAnomalyPieData] = useState([]);
  const [meterTypes, setMeterTypes] = useState([
    { value: "ALL", Label: "All" },
  ]);

  const [timePeriods] = useState([
    { value: "Current", Label: "Latest" },
    { value: "Last 14 Days", Label: "L 2W" },
    { value: "Last 30 Days", Label: "L 1M" },
  ]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getAnomalyCategory();
        const uniqueCategories = [
          { value: "ALL", Label: "All" },
          ...Array.from(
            new Map(
              data.map((item) => [
                item.class_display_name,
                {
                  value: item.class_display_name,
                  Label: item.class_display_name,
                },
              ]),
            ).values(),
          ),
        ];
        setMeterTypes(uniqueCategories);
      } catch (error) {
        console.error("Error fetching anomaly categories:", error);
      }
    };
    fetchCategories();
  }, [selectedDistrict]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await getWaterLeak(selectedMeterType, selectedTimePeriod);
        setDataLeak(data);
        if (selectedMeterType === "ALL") {
          setLeakTableData([]);
          setAnomalyPieData(Array.isArray(data) ? data : []);
        } else {
          setAnomalyPieData([]);
          setLeakTableData(data?.meters || []);
        }
      } catch (error) {
        console.error("Error fetching water leak data:", error);
        setDataLeak([]);
        setLeakTableData([]);
        setAnomalyPieData([]);
      } finally {
        setTimeout(() => setIsLoading(false), 400);
      }
    };
    fetchData();
  }, [selectedMeterType, selectedTimePeriod, selectedDistrict]);

  const segments = useMemo(() => {
    if (selectedMeterType !== "ALL") return [];
    let cumulativePercentage = 0;
    return anomalyPieData.map((item, index) => {
      const percentage = safeRound(item.leak_percentage);
      const startAngle = (cumulativePercentage / 100) * 360;
      cumulativePercentage += percentage;
      const endAngle = (cumulativePercentage / 100) * 360;

      const numericPrice = safeRound(item.total_price);
      const formattedPrice = `$${numericPrice.toLocaleString("en-US")}`;

      return {
        category: item.category,
        leaks: item.leak_meter_count,
        percentage: percentage,
        loss: formattedPrice,
        totalWaterLoss: safeRound(item.total_water_loss_gallons),
        color: PIE_COLORS[index % PIE_COLORS.length],
        startAngle,
        endAngle,
      };
    });
  }, [anomalyPieData, selectedMeterType, colors]);

  const totalActiveLeaks =
    selectedMeterType === "ALL"
      ? anomalyPieData.reduce(
          (sum, item) => sum + Number(item.leak_meter_count || 0),
          0,
        )
      : leakTableData.length;

  const totalWaterLost =
    selectedMeterType === "ALL"
      ? anomalyPieData.reduce(
          (sum, item) => sum + safeRound(item.total_water_loss_gallons),
          0,
        )
      : safeRound(dataLeak?.total_water_loss);

  const totalLossAmount =
    selectedMeterType === "ALL"
      ? anomalyPieData.reduce(
          (sum, item) => sum + safeRound(item.total_price),
          0,
        )
      : safeRound(dataLeak?.total_price);

  // const createPieSlice = (startAngle, endAngle, radius) => {
  //   const startRadians = (startAngle - 90) * (Math.PI / 180);
  //   const endRadians = (endAngle - 90) * (Math.PI / 180);
  //   const x1 = 100 + radius * Math.cos(startRadians);
  //   const y1 = 100 + radius * Math.sin(startRadians);
  //   const x2 = 100 + radius * Math.cos(endRadians);
  //   const y2 = 100 + radius * Math.sin(endRadians);
  //   const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
  //   return `M 100 100 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  // };
  const createPieSlice = (startAngle, endAngle, radius) => {
    // Check if it's a full 360 degree circle (100% case)
    if (endAngle - startAngle >= 360) {
      return `M 100, 100 m -${radius}, 0 a ${radius},${radius} 0 1,0 ${radius * 2},0 a ${radius},${radius} 0 1,0 -${radius * 2},0`;
    }

    const startRadians = (startAngle - 90) * (Math.PI / 180);
    const endRadians = (endAngle - 90) * (Math.PI / 180);

    const x1 = 100 + radius * Math.cos(startRadians);
    const y1 = 100 + radius * Math.sin(startRadians);
    const x2 = 100 + radius * Math.cos(endRadians);
    const y2 = 100 + radius * Math.sin(endRadians);

    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return `M 100 100 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  };
  return (
    <motion.div
      onClick={onClick}
      className="rounded-xl p-6 transition-colors duration-300 relative overflow-hidden flex flex-col group h-full min-h-[550px]"
      style={{
        backgroundColor: theme === "white" ? "#ffffff" : colors.glassEffect,
        backdropFilter: "blur(10px)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: colors.border,
      }}
      initial={{ opacity: 0, x: -50, rotateY: 15 }}
      animate={{ opacity: 1, x: 0, rotateY: 0 }}
      transition={{ duration: 0.7, delay: 0.4, type: "spring", bounce: 0.4 }}
      whileHover={{
        x: 10,
        transition: { type: "spring", stiffness: 300 },
      }}
    >
      <div
        className="absolute top-0 left-0 h-[2px] w-0 group-hover:w-full transition-all duration-[400ms] ease-out"
        style={{ backgroundImage: colors.gradient }}
      />

      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-5">
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundImage: colors.gradient }}
            >
              <Droplet className="w-5 h-5 text-white" />
            </div>
            <h3
              className="text-2xl font-bold tracking-tight"
              style={{ color: colors.text }}
            >
              Leak Flow
            </h3>
          </div>
          <div className="flex gap-4 flex-wrap">
            <select
              value={selectedMeterType}
              aria-label="Select meter type"
              onChange={(e) => setSelectedMeterType(e.target.value)}
              className="max-[767px]:w-full px-3 py-2 rounded-lg text-sm font-medium border transition-colors cursor-pointer"
              style={{
                background: colors.background,
                borderColor: colors.border,
                color: colors.text,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {meterTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.Label}
                </option>
              ))}
            </select>
            {/* <select
              value={selectedTimePeriod}
              onChange={(e) => setSelectedTimePeriod(e.target.value)}
              className="max-[767px]:w-full px-3 py-2 rounded-lg text-sm font-medium border transition-colors cursor-pointer"
              style={{
                background: colors.background,
                borderColor: colors.border,
                color: colors.text,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {timePeriods.map((period) => (
                <option key={period.value} value={period.value}>
                  {period.Label}
                </option>
              ))}
            </select> */}
            <div
              className="flex items-center p-1 rounded-lg border bg-opacity-20 shrink-0"
               style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {timePeriods.map((tp) => {
                const isActive = selectedTimePeriod === tp.value;
                return (
                  <button
                    key={tp.value}
                    onClick={() => setSelectedTimePeriod(tp.value)}
                    className="px-2.5 py-1 rounded-md text-[10px] min-[1921px]:text-[12px] font-black transition-all duration-200 uppercase cursor-pointer"
                    style={{
                      backgroundColor: isActive
                        ? colors.primary
                        : "transparent",
                      color: isActive ? "#ffffff" : colors.textSecondary,
                      boxShadow: isActive
                        ? "0 2px 4px rgba(0,0,0,0.3)"
                        : "none",
                    }}
                  >
                    {tp.Label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="relative flex-1 flex flex-col">
          <AnimatePresence mode="wait">
            {/* {isLoading ? (
              <motion.div
                key="loader"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex flex-col items-center justify-center z-20"
              >
                <div className="relative w-20 h-20">
                  <svg
                    className="absolute inset-0 w-full h-full"
                    viewBox="0 0 100 100"
                  >
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke={colors.border}
                      strokeWidth="4"
                      strokeOpacity="0.2"
                    />
                  </svg>
                  <motion.svg
                    className="absolute inset-0 w-full h-full"
                    viewBox="0 0 100 100"
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke={colors.primary}
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeDasharray="70 200"
                    />
                  </motion.svg>
                </div>
                <span
                  className="mt-6 text-xs font-bold tracking-widest uppercase opacity-70"
                  style={{ color: colors.textSecondary }}
                >
                  Analysing Data
                </span>
              </motion.div>
            ) : ( */}
            {isLoading ? (
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <Spin size="large" />
              </div>
            ) : (
              <motion.div
                key="content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col h-full"
              >
                <div className="mb-4">
                  <div className="flex items-baseline gap-2">
                    <span
                      className="text-4xl font-bold"
                      style={{
                        backgroundImage: colors.gradient,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {totalActiveLeaks.toLocaleString("en-US")}
                    </span>
                    <span
                      className="text-sm"
                      style={{ color: colors.textSecondary }}
                    >
                      Active Leaks
                    </span>
                  </div>
                </div>

                {selectedMeterType === "ALL" && (
                  <div className="flex flex-col items-center mb-4 flex-1">
                    <div className="relative flex-shrink-0">
                      <svg width="200" height="200" viewBox="0 0 200 200">
                        <defs>
                          {segments.map((item, index) => (
                            <linearGradient
                              key={index}
                              id={`leakPieGradient-${index}`}
                              x1="0%"
                              y1="0%"
                              x2="100%"
                              y2="100%"
                            >
                              <stop offset="0%" stopColor={item.color} />
                              <stop
                                offset="100%"
                                stopColor={item.color}
                                stopOpacity="0.6"
                              />
                            </linearGradient>
                          ))}
                        </defs>
                        {segments.map((item, index) => (
                          <motion.path
                            key={index}
                            d={createPieSlice(
                              item.startAngle,
                              item.endAngle,
                              80,
                            )}
                            fill={`url(#leakPieGradient-${index})`}
                            stroke={theme === "white" ? "#e0e0e0" : "#1C1C1C"}
                            strokeWidth="2"
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                            animate={{
                              opacity:
                                hoveredIndex === index
                                  ? 1
                                  : hoveredIndex !== null
                                    ? 0.5
                                    : 1,
                              scale: hoveredIndex === index ? 1.08 : 1,
                            }}
                            style={{ transformOrigin: "100px 100px" }}
                          />
                        ))}
                        <circle
                          cx="100"
                          cy="100"
                          r="30"
                          fill={
                            theme === "white" ? "#f8f9fa" : colors.background
                          }
                        />
                        <AnimatePresence>
                          {hoveredIndex !== null && (
                            <motion.text
                              initial={{ opacity: 0, y: 5 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              x="100"
                              y="105"
                              textAnchor="middle"
                              className="text-[14px] font-bold"
                              style={{
                                fill: colors.text,
                                pointerEvents: "none",
                              }}
                            >
                              {segments[hoveredIndex].percentage}%
                            </motion.text>
                          )}
                        </AnimatePresence>
                      </svg>
                    </div>

                    <div className="w-full grid grid-cols-2 gap-2 mt-2 max-[567px]:grid-cols-1">
                      {segments.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 p-2 rounded-lg backdrop-blur-sm border transition-all group cursor-pointer"
                          style={{
                            backgroundColor:
                              hoveredIndex === index
                                ? `${colors.primary}20`
                                : `${colors.primary}0d`,
                            borderColor: colors.border,
                            transform:
                              hoveredIndex === index
                                ? "scale(1.03)"
                                : "scale(1)",
                          }}
                          onMouseEnter={() => setHoveredIndex(index)}
                          onMouseLeave={() => setHoveredIndex(null)}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/flow-health", {
                              state: { tab: "leak", category: item.category },
                            });
                          }}
                        >
                          <div
                            className="w-3 h-3 rounded flex-shrink-0"
                            style={{ backgroundColor: item.color }}
                          />
                          <div className="flex items-center justify-between min-w-0 flex-1">
                            <div>
                              <span
                                className="text-xs font-medium truncate block"
                                style={{ color: colors.text }}
                              >
                                {item.category} [{item.leaks}]
                              </span>
                              <div className="flex items-baseline gap-1.5">
                                <span
                                  className="text-xs font-bold block"
                                  style={{ color: colors.text }}
                                >
                                  {item.totalWaterLoss.toLocaleString("en-US")}{" "}
                                  Gallons
                                </span>
                                <span
                                  className="text-[10px]"
                                  style={{ color: colors.textSecondary }}
                                >
                                  ({item.percentage}%)
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <span
                                className="text-xs font-bold"
                                style={{ color: colors.text }}
                              >
                                {item.loss}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mb-4 mt-auto max-[451px]:grid-cols-1">
                  <div
                    className="backdrop-blur-sm rounded-lg p-4"
                    style={{
                      backgroundColor: `${colors.primary}0d`,
                      border: `1px solid ${colors.primary}1a`,
                    }}
                  >
                    <span
                      className="text-xs block mb-1"
                      style={{ color: colors.textSecondary }}
                    >
                      Total Water Lost
                    </span>
                    <div
                      className="text-2xl font-bold"
                      style={{ color: colors.text }}
                    >
                      {totalWaterLost.toLocaleString("en-US")}
                    </div>
                    <span
                      className="text-xs"
                      style={{ color: colors.textSecondary }}
                    >
                      Gallons
                    </span>
                  </div>
                  <div
                    className="backdrop-blur-sm rounded-lg p-4"
                    style={{
                      backgroundColor: `${colors.primary}0d`,
                      border: `1px solid ${colors.primary}1a`,
                    }}
                  >
                    <span
                      className="text-xs block mb-1"
                      style={{ color: colors.textSecondary }}
                    >
                      Total Loss
                    </span>
                    <div
                      className="text-2xl font-bold"
                      style={{ color: colors.text }}
                    >
                      ${totalLossAmount.toLocaleString("en-US")}
                    </div>
                    <span
                      className="text-xs"
                      style={{ color: colors.textSecondary }}
                    >
                      USD
                    </span>
                  </div>
                </div>

                {selectedMeterType !== "ALL" && leakTableData.length > 0 && (
                  <div className="overflow-auto flex-1 max-h-[280px]">
                    <table className="w-full text-sm">
                      <thead>
                        <tr
                          style={{ borderBottom: `1px solid ${colors.border}` }}
                        >
                          <th
                            className="text-left py-2 px-3"
                            style={{ color: colors.textSecondary }}
                          >
                            Meter ID
                          </th>
                          <th
                            className="text-left py-2 px-3"
                            style={{ color: colors.textSecondary }}
                          >
                            Leak Rate
                          </th>
                          <th
                            className="text-left py-2 px-3"
                            style={{ color: colors.textSecondary }}
                          >
                            Detected
                          </th>
                          <th
                            className="text-left py-2 px-3"
                            style={{ color: colors.textSecondary }}
                          >
                            Duration
                          </th>
                          <th
                            className="text-left py-2 px-3"
                            style={{ color: colors.textSecondary }}
                          >
                            Total Loss
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {leakTableData.map((meter) => (
                          <tr
                            key={meter.meter_id}
                            className="border-b cursor-pointer transition-colors"
                            style={{ borderColor: colors.border }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor = `${colors.primary}08`)
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "transparent")
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/meter-leak-details?category=${selectedMeterType}&meter_id=${meter.meter_id}&time_range=${selectedTimePeriod}&selected_time=${formatDateToYYYYMMDD(meter.error_time)}`,
                                {
                                  state: {
                                    water_loss_gallons:
                                      meter.water_loss_gallons,
                                    price: meter.price,
                                    leak_rate: meter.leak_rate,
                                  },
                                },
                              );
                            }}
                          >
                            <td
                              className="py-3 px-3 font-bold underline decoration-1 underline-offset-4"
                              style={{ color: colors.primary }}
                            >
                              {meter.meter_id}
                            </td>
                            <td
                              className="py-3 px-3"
                              style={{ color: colors.text }}
                            >
                              {safeRound(meter.leak_rate).toLocaleString(
                                "en-US",
                              )}{" "}
                              GPH
                            </td>
                            <td
                              className="py-3 px-3"
                              style={{ color: colors.text }}
                            >
                              {formatDateCustom(meter.error_time)}
                            </td>
                            {/* Duration with comma separation */}
                            <td
                              className="py-3 px-3"
                              style={{ color: colors.text }}
                            >
                              {meter?.duration_hours}
                            </td>
                            <td className="py-3 px-3">
                              <span
                                className="px-2 py-1 rounded text-xs font-medium"
                                style={{
                                  color: "#ef4444",
                                  backgroundColor:
                                    theme === "white" ? "#fee2e2" : "#7f1d1d20",
                                }}
                              >
                                $
                                {safeRound(meter.price).toLocaleString("en-US")}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
